import { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import Draggable from 'react-draggable';
import { PlaceholderPosition, PageBackground } from '@/types/quote';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { PlaceholderPropertyEditor } from './PlaceholderPropertyEditor';
import { NudgeControls } from './NudgeControls';
import { PlaceholderHelpPopup } from './PlaceholderHelpPopup';
import { PageOrderPanel } from './PageOrderPanel';
import { getAllPlaceholderKeys, getSampleData, createDefaultPlaceholder, getDefaultPlaceholderSize } from '@/lib/pdfTemplateRenderer';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Eye, EyeOff, Grid3x3, AlignLeft, AlignRight, AlignVerticalJustifyCenter, AlignHorizontalJustifyCenter, AlignStartVertical, AlignEndVertical, ArrowLeftRight, ArrowUpDown, Trash2, Plus, ChevronDown, ChevronUp, Info, Search, Undo2, Redo2, ImageIcon, Table, HelpCircle, PanelLeft, FileText, Copy, ClipboardPaste, Globe, Type } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { useUndoRedo } from '@/hooks/useUndoRedo';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Set PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const KeyboardShortcut = ({ keys, description }: { keys: string[]; description: string }) => (
  <div className="flex items-center gap-1.5 text-[11px]">
    {keys.map((key, i) => (
      <span key={i} className="flex items-center gap-0.5">
        {i > 0 && <span className="text-muted-foreground">+</span>}
        <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono border border-border">
          {key}
        </kbd>
      </span>
    ))}
    <span className="text-muted-foreground ml-1">{description}</span>
  </div>
);

interface PDFTemplateDesignerProps {
  pdfUrl: string | null; // Now optional - can be null for blank multi-page templates
  initialPositions?: PlaceholderPosition[];
  pageCount?: number;
  pageBackgrounds?: PageBackground[];
  onSave: (positions: PlaceholderPosition[], pageCount: number, pageBackgrounds: PageBackground[]) => void;
  onCancel: () => void;
}

export const PDFTemplateDesigner = ({
  pdfUrl,
  initialPositions = [],
  pageCount: initialPageCount = 1,
  pageBackgrounds: initialPageBackgrounds = [],
  onSave,
  onCancel
}: PDFTemplateDesignerProps) => {
  // Page constants (A4 dimensions)
  const PAGE_WIDTH = 595;
  const PAGE_HEIGHT = 842;
  const SNAP_THRESHOLD = 8; // pixels - distance at which snapping activates
  
  const [numPages, setNumPages] = useState(pdfUrl ? 0 : initialPageCount);
  const [currentPage, setCurrentPage] = useState(1);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  
  // Multi-page state management
  const [localPageCount, setLocalPageCount] = useState(initialPageCount || 1);
  const [localPageBackgrounds, setLocalPageBackgrounds] = useState<PageBackground[]>(
    initialPageBackgrounds?.length > 0 
      ? initialPageBackgrounds 
      : [{ page: 1, type: 'blank', color: '#ffffff' }]
  );
  
  // Undo/Redo for placeholders
  const {
    state: placeholders,
    set: setPlaceholdersWithHistory,
    setWithoutHistory: setPlaceholdersLive,
    commit: commitPlaceholderHistory,
    undo: undoPlaceholders,
    redo: redoPlaceholders,
    reset: resetPlaceholders,
    canUndo,
    canRedo
  } = useUndoRedo<PlaceholderPosition[]>(initialPositions, { maxHistory: 50 });
  
  // Wrapper for backwards compatibility
  const setPlaceholders = setPlaceholdersWithHistory;
  
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  
  // Clipboard for copy/paste functionality
  const [clipboard, setClipboard] = useState<PlaceholderPosition[]>([]);
  
  // Static text functionality
  const [staticTextCounter, setStaticTextCounter] = useState(1);
  const [newStaticText, setNewStaticText] = useState('');
  
  // Snap guides state for magnetic snapping
  const [snapGuides, setSnapGuides] = useState<{
    vertical: number[];
    horizontal: number[];
  }>({ vertical: [], horizontal: [] });
  
  const pdfPageRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Drag session management
  const dragSessionRef = useRef<{
    isDragging: boolean;
    selectedIndices: Set<number>;
    startPositions: Map<number, { x: number; y: number }>;
  } | null>(null);
  const isDraggingPlaceholderRef = useRef(false);
  const rafPendingRef = useRef(false);

  // Grid settings
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(10); // pixels
  const [nudgeStepSize, setNudgeStepSize] = useState(10); // pixels for nudge controls

  // Snap utility functions
  const snapToGridValue = (value: number, gridSize: number): number => {
    return Math.round(value / gridSize) * gridSize;
  };

  const snapPosition = (x: number, y: number): { x: number; y: number } => {
    if (!snapToGrid) return { x, y };
    return {
      x: snapToGridValue(x, gridSize),
      y: snapToGridValue(y, gridSize)
    };
  };

  const snapDimensions = (width: number, height: number): { width: number; height: number } => {
    if (!snapToGrid) return { width, height };
    return {
      width: snapToGridValue(width, gridSize),
      height: snapToGridValue(height, gridSize)
    };
  };

  // Clamp position to page bounds (A4 dimensions in unscaled units)
  const clampToPage = (x: number, y: number, width: number, height: number) => {
    const maxX = Math.max(0, 595 - width);
    const maxY = Math.max(0, 842 - height);
    return {
      x: Math.max(0, Math.min(x, maxX)),
      y: Math.max(0, Math.min(y, maxY))
    };
  };

  // Commit snap and clamp for indices
  const commitSnap = (indices: Set<number>) => {
    setPlaceholders(prev => {
      const updated = [...prev];
      indices.forEach(i => {
        const p = updated[i];
        const snapped = snapPosition(p.x, p.y);
        const clamped = clampToPage(snapped.x, snapped.y, p.width, p.height);
        updated[i] = { ...updated[i], x: clamped.x, y: clamped.y };
      });
      return updated;
    });
  };

  // Selection helper functions
  const isSelected = (index: number) => selectedIndices.has(index);
  
  const toggleSelection = (index: number, ctrlKey: boolean, shiftKey: boolean) => {
    // Suppress click-toggle if we just finished dragging
    if (isDraggingPlaceholderRef.current) {
      return;
    }

    if (shiftKey && lastSelectedIndex !== null) {
      // Range selection
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const newSelection = new Set(selectedIndices);
      for (let i = start; i <= end; i++) {
        if (i < placeholders.length) newSelection.add(i);
      }
      setSelectedIndices(newSelection);
    } else if (ctrlKey) {
      // Toggle individual
      const newSelection = new Set(selectedIndices);
      if (newSelection.has(index)) {
        newSelection.delete(index);
      } else {
        newSelection.add(index);
      }
      setSelectedIndices(newSelection);
      setLastSelectedIndex(index);
    } else {
      // Single selection
      setSelectedIndices(new Set([index]));
      setLastSelectedIndex(index);
    }
  };

  const clearSelection = () => {
    setSelectedIndices(new Set());
    setLastSelectedIndex(null);
  };

  const selectMultiple = (indices: number[]) => {
    setSelectedIndices(new Set(indices));
  };

  const deleteSelected = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => prev.filter((_, i) => !selectedIndices.has(i)));
    clearSelection();
    toast.success(`${selectedIndices.size} veld(en) verwijderd`);
  };

  // Copy selected placeholders to clipboard
  const copySelected = useCallback(() => {
    if (selectedIndices.size === 0) return;
    
    const copied = Array.from(selectedIndices).map(i => ({
      ...placeholders[i]
    }));
    
    setClipboard(copied);
    toast.success(`${copied.length} veld(en) gekopieerd`);
  }, [selectedIndices, placeholders]);

  // Paste clipboard to current page
  const pasteClipboard = useCallback(() => {
    if (clipboard.length === 0) {
      toast.info('Niets om te plakken - kopieer eerst een veld');
      return;
    }
    
    // Calculate if we're pasting on the same page (add offset) or different page (exact position)
    const newPlaceholders = clipboard.map((p) => ({
      ...p,
      page: currentPage,
      // Add small offset only if pasting on same page to avoid exact overlap
      x: p.page === currentPage ? Math.min(595 - p.width, p.x + 15) : p.x,
      y: p.page === currentPage ? Math.min(842 - p.height, p.y + 15) : p.y,
      isGlobal: false, // Pasted items are not global by default
    }));
    
    const startIndex = placeholders.length;
    
    setPlaceholders(prev => [...prev, ...newPlaceholders]);
    
    // Select the newly pasted placeholders
    const newIndices = new Set(newPlaceholders.map((_, i) => startIndex + i));
    setSelectedIndices(newIndices);
    
    toast.success(`${newPlaceholders.length} veld(en) geplakt op pagina ${currentPage}`);
  }, [clipboard, currentPage, placeholders.length, setPlaceholders]);

  // Add static text element
  const addStaticText = useCallback((text: string) => {
    const key = `{{static_text_${staticTextCounter}}}`;
    setStaticTextCounter(prev => prev + 1);
    
    const newPlaceholder: PlaceholderPosition = {
      key,
      page: currentPage,
      x: 50,
      y: 50,
      width: 100,
      height: 25,
      fontSize: 12,
      fontWeight: 'normal',
      color: '#000000',
      align: 'left',
      staticValue: text
    };
    
    const startIndex = placeholders.length;
    setPlaceholders(prev => [...prev, newPlaceholder]);
    setSelectedIndices(new Set([startIndex]));
    setLastSelectedIndex(startIndex);
    
    toast.success(`Vaste tekst "${text}" toegevoegd`);
  }, [staticTextCounter, currentPage, placeholders.length, setPlaceholders]);

  // Nudge functions
  const handleNudge = (direction: 'up' | 'down' | 'left' | 'right', amount: number) => {
    if (selectedIndices.size === 0) return;
    
    setPlaceholders(prev => {
      const updated = [...prev];
      selectedIndices.forEach(i => {
        const p = updated[i];
        let newX = p.x;
        let newY = p.y;
        
        switch (direction) {
          case 'up':
            newY = Math.max(0, p.y - amount);
            break;
          case 'down':
            newY = Math.min(842 - p.height, p.y + amount);
            break;
          case 'left':
            newX = Math.max(0, p.x - amount);
            break;
          case 'right':
            newX = Math.min(595 - p.width, p.x + amount);
            break;
        }
        
        const snapped = snapPosition(newX, newY);
        updated[i] = { ...updated[i], x: snapped.x, y: snapped.y };
      });
      return updated;
    });
  };

  const handleCenter = () => {
    if (selectedIndices.size === 0) return;
    
    setPlaceholders(prev => {
      const updated = [...prev];
      selectedIndices.forEach(i => {
        const p = updated[i];
        const centerX = (595 - p.width) / 2;
        const centerY = (842 - p.height) / 2;
        const snapped = snapPosition(centerX, centerY);
        updated[i] = { ...updated[i], x: snapped.x, y: snapped.y };
      });
      return updated;
    });
    toast.success('Gecentreerd op pagina');
  };

  // Magnetic snap guide calculation
  const calculateSnapGuides = useCallback((
    draggedIndex: number,
    newX: number,
    newY: number,
    width: number,
    height: number
  ): { x: number; y: number; guides: { vertical: number[]; horizontal: number[] } } => {
    const otherPlaceholders = placeholders.filter((p, i) => i !== draggedIndex && p.page === currentPage);
    
    let snappedX = newX;
    let snappedY = newY;
    const activeGuides = { vertical: [] as number[], horizontal: [] as number[] };
    
    // Edges and centers of the dragged element
    const dragEdges = {
      left: newX,
      right: newX + width,
      centerX: newX + width / 2,
      top: newY,
      bottom: newY + height,
      centerY: newY + height / 2
    };
    
    // Check against all other placeholders
    for (const other of otherPlaceholders) {
      const otherEdges = {
        left: other.x,
        right: other.x + other.width,
        centerX: other.x + other.width / 2,
        top: other.y,
        bottom: other.y + other.height,
        centerY: other.y + other.height / 2
      };
      
      // Vertical snapping (X-axis)
      if (Math.abs(dragEdges.left - otherEdges.left) < SNAP_THRESHOLD) {
        snappedX = otherEdges.left;
        activeGuides.vertical.push(otherEdges.left);
      }
      if (Math.abs(dragEdges.right - otherEdges.right) < SNAP_THRESHOLD) {
        snappedX = otherEdges.right - width;
        activeGuides.vertical.push(otherEdges.right);
      }
      if (Math.abs(dragEdges.right - otherEdges.left) < SNAP_THRESHOLD) {
        snappedX = otherEdges.left - width;
        activeGuides.vertical.push(otherEdges.left);
      }
      if (Math.abs(dragEdges.left - otherEdges.right) < SNAP_THRESHOLD) {
        snappedX = otherEdges.right;
        activeGuides.vertical.push(otherEdges.right);
      }
      if (Math.abs(dragEdges.centerX - otherEdges.centerX) < SNAP_THRESHOLD) {
        snappedX = otherEdges.centerX - width / 2;
        activeGuides.vertical.push(otherEdges.centerX);
      }
      
      // Horizontal snapping (Y-axis)
      if (Math.abs(dragEdges.top - otherEdges.top) < SNAP_THRESHOLD) {
        snappedY = otherEdges.top;
        activeGuides.horizontal.push(otherEdges.top);
      }
      if (Math.abs(dragEdges.bottom - otherEdges.bottom) < SNAP_THRESHOLD) {
        snappedY = otherEdges.bottom - height;
        activeGuides.horizontal.push(otherEdges.bottom);
      }
      if (Math.abs(dragEdges.bottom - otherEdges.top) < SNAP_THRESHOLD) {
        snappedY = otherEdges.top - height;
        activeGuides.horizontal.push(otherEdges.top);
      }
      if (Math.abs(dragEdges.top - otherEdges.bottom) < SNAP_THRESHOLD) {
        snappedY = otherEdges.bottom;
        activeGuides.horizontal.push(otherEdges.bottom);
      }
      if (Math.abs(dragEdges.centerY - otherEdges.centerY) < SNAP_THRESHOLD) {
        snappedY = otherEdges.centerY - height / 2;
        activeGuides.horizontal.push(otherEdges.centerY);
      }
    }
    
    // Also snap to page edges and center
    if (Math.abs(dragEdges.left) < SNAP_THRESHOLD) {
      snappedX = 0;
      activeGuides.vertical.push(0);
    }
    if (Math.abs(dragEdges.right - PAGE_WIDTH) < SNAP_THRESHOLD) {
      snappedX = PAGE_WIDTH - width;
      activeGuides.vertical.push(PAGE_WIDTH);
    }
    if (Math.abs(dragEdges.centerX - PAGE_WIDTH / 2) < SNAP_THRESHOLD) {
      snappedX = PAGE_WIDTH / 2 - width / 2;
      activeGuides.vertical.push(PAGE_WIDTH / 2);
    }
    if (Math.abs(dragEdges.top) < SNAP_THRESHOLD) {
      snappedY = 0;
      activeGuides.horizontal.push(0);
    }
    if (Math.abs(dragEdges.bottom - PAGE_HEIGHT) < SNAP_THRESHOLD) {
      snappedY = PAGE_HEIGHT - height;
      activeGuides.horizontal.push(PAGE_HEIGHT);
    }
    if (Math.abs(dragEdges.centerY - PAGE_HEIGHT / 2) < SNAP_THRESHOLD) {
      snappedY = PAGE_HEIGHT / 2 - height / 2;
      activeGuides.horizontal.push(PAGE_HEIGHT / 2);
    }
    
    return { x: snappedX, y: snappedY, guides: activeGuides };
  }, [placeholders, currentPage, SNAP_THRESHOLD, PAGE_WIDTH, PAGE_HEIGHT]);

  // Keyboard shortcuts useEffect is moved after alignment functions are defined (below)

  // Log scale changes
  useEffect(() => {
    console.log('[Scale Changed]', {
      scale,
      percentage: Math.round(scale * 100),
      pdfWidth: 595 * scale,
      pdfHeight: 842 * scale,
      timestamp: new Date().toISOString()
    });
  }, [scale]);

  const availablePlaceholders = getAllPlaceholderKeys();

  const handleDragStart = (key: string) => {
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (!pdfPageRef.current || !scrollContainerRef.current) {
      console.error('[Drop] Missing refs', { 
        pdfPageRef: !!pdfPageRef.current, 
        scrollContainerRef: !!scrollContainerRef.current 
      });
      toast.error('Kon veld niet plaatsen - missing refs');
      return;
    }

    const pdfRect = pdfPageRef.current.getBoundingClientRect();
    const scrollContainer = scrollContainerRef.current;
    
    // Calculate position relative to PDF, accounting for scroll
    const scrollTop = scrollContainer.scrollTop;
    const scrollLeft = scrollContainer.scrollLeft;
    
    const x = e.clientX - pdfRect.left + scrollLeft;
    const y = e.clientY - pdfRect.top + scrollTop;

    console.log('[Drop] Raw coordinates', {
      clientX: e.clientX,
      clientY: e.clientY,
      pdfRect,
      scrollTop,
      scrollLeft,
      calculatedX: x,
      calculatedY: y,
      scale
    });

    const key = e.dataTransfer.getData('text/plain');
    if (!key) {
      console.error('[Drop] No key in dataTransfer');
      toast.error('Kon veld niet plaatsen - missing key');
      return;
    }

    // Get default placeholder dimensions first
    const tempPlaceholder = createDefaultPlaceholder(key, 0, 0);
    const placeholderWidth = tempPlaceholder.width;
    const placeholderHeight = tempPlaceholder.height;
    
    // Place in center of A4 page (595x842), accounting for placeholder size
    const centerX = (595 - placeholderWidth) / 2;
    const centerY = (842 - placeholderHeight) / 2;
    
    // Snap center position to grid
    const snapped = snapPosition(centerX, centerY);
    
    console.log('[Drop] Creating placeholder', {
      key,
      placeholderSize: { width: placeholderWidth, height: placeholderHeight },
      centerPosition: { x: centerX, y: centerY },
      snappedPosition: snapped,
      page: currentPage
    });

    const newPlaceholder = createDefaultPlaceholder(key, snapped.x, snapped.y);
    newPlaceholder.page = currentPage;

    setPlaceholders(prev => {
      const newArray = [...prev, newPlaceholder];
      console.log('[Drop] Updated placeholders', {
        previousCount: prev.length,
        newCount: newArray.length,
        newPlaceholder
      });
      return newArray;
    });
    
    // Select the newly added placeholder
    const newIndex = placeholders.length;
    setSelectedIndices(new Set([newIndex]));
    setLastSelectedIndex(newIndex);
    
    toast.success(`Veld "${key}" toegevoegd in midden van pagina`);
  };

  const handlePlaceholderMove = (index: number, x: number, y: number) => {
    const snapped = snapPosition(x, y);
    setPlaceholders(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], x: snapped.x, y: snapped.y };
      return updated;
    });
  };

  const handlePlaceholderUpdate = (index: number, updates: Partial<PlaceholderPosition>) => {
    setPlaceholders(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };
      return updated;
    });
  };

  const handlePlaceholderDelete = (index: number) => {
    setPlaceholders(prev => prev.filter((_, i) => i !== index));
    clearSelection();
    toast.success('Veld verwijderd');
  };

  const handleResize = (index: number, width: number, height: number) => {
    const snapped = snapDimensions(width, height);
    setPlaceholders(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], width: snapped.width, height: snapped.height };
      return updated;
    });
  };

  // Alignment functions - single item aligns to page, multiple items align to each other

  const alignLeft = () => {
    if (selectedIndices.size === 0) return;
    
    setPlaceholders(prev => {
      const updated = [...prev];
      
      if (selectedIndices.size === 1) {
        // Single item: align to page left edge
        const idx = Array.from(selectedIndices)[0];
        const snappedX = snapToGridValue(0, gridSize);
        updated[idx] = { ...updated[idx], x: snappedX };
      } else {
        // Multiple items: align to leftmost item
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const minX = Math.min(...selected.map(p => p.x));
        const snappedX = snapToGridValue(minX, gridSize);
        selectedIndices.forEach(i => {
          updated[i] = { ...updated[i], x: snappedX };
        });
      }
      
      return updated;
    });
    toast.success(selectedIndices.size === 1 ? 'Uitgelijnd aan linkerkant pagina' : 'Links uitgelijnd');
  };

  const alignRight = () => {
    if (selectedIndices.size === 0) return;
    
    setPlaceholders(prev => {
      const updated = [...prev];
      
      if (selectedIndices.size === 1) {
        // Single item: align to page right edge
        const idx = Array.from(selectedIndices)[0];
        const snappedX = snapToGridValue(PAGE_WIDTH - updated[idx].width, gridSize);
        updated[idx] = { ...updated[idx], x: snappedX };
      } else {
        // Multiple items: align to rightmost edge
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const maxX = Math.max(...selected.map(p => p.x + p.width));
        const snappedX = snapToGridValue(maxX, gridSize);
        selectedIndices.forEach(i => {
          updated[i] = { ...updated[i], x: snappedX - updated[i].width };
        });
      }
      
      return updated;
    });
    toast.success(selectedIndices.size === 1 ? 'Uitgelijnd aan rechterkant pagina' : 'Rechts uitgelijnd');
  };

  const alignTop = () => {
    if (selectedIndices.size === 0) return;
    
    setPlaceholders(prev => {
      const updated = [...prev];
      
      if (selectedIndices.size === 1) {
        // Single item: align to page top
        const idx = Array.from(selectedIndices)[0];
        const snappedY = snapToGridValue(0, gridSize);
        updated[idx] = { ...updated[idx], y: snappedY };
      } else {
        // Multiple items: align to topmost
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const minY = Math.min(...selected.map(p => p.y));
        const snappedY = snapToGridValue(minY, gridSize);
        selectedIndices.forEach(i => {
          updated[i] = { ...updated[i], y: snappedY };
        });
      }
      
      return updated;
    });
    toast.success(selectedIndices.size === 1 ? 'Uitgelijnd aan bovenkant pagina' : 'Boven uitgelijnd');
  };

  const alignBottom = () => {
    if (selectedIndices.size === 0) return;
    
    setPlaceholders(prev => {
      const updated = [...prev];
      
      if (selectedIndices.size === 1) {
        // Single item: align to page bottom
        const idx = Array.from(selectedIndices)[0];
        const snappedY = snapToGridValue(PAGE_HEIGHT - updated[idx].height, gridSize);
        updated[idx] = { ...updated[idx], y: snappedY };
      } else {
        // Multiple items: align to bottommost
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const maxY = Math.max(...selected.map(p => p.y + p.height));
        const snappedY = snapToGridValue(maxY, gridSize);
        selectedIndices.forEach(i => {
          updated[i] = { ...updated[i], y: snappedY - updated[i].height };
        });
      }
      
      return updated;
    });
    toast.success(selectedIndices.size === 1 ? 'Uitgelijnd aan onderkant pagina' : 'Onder uitgelijnd');
  };

  const alignCenterH = () => {
    if (selectedIndices.size === 0) return;
    
    setPlaceholders(prev => {
      const updated = [...prev];
      
      if (selectedIndices.size === 1) {
        // Single item: center horizontally on page
        const idx = Array.from(selectedIndices)[0];
        const snappedX = snapToGridValue((PAGE_WIDTH - updated[idx].width) / 2, gridSize);
        updated[idx] = { ...updated[idx], x: snappedX };
      } else {
        // Multiple items: center relative to each other
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const minX = Math.min(...selected.map(p => p.x));
        const maxX = Math.max(...selected.map(p => p.x + p.width));
        const centerX = (minX + maxX) / 2;
        selectedIndices.forEach(i => {
          const snappedX = snapToGridValue(centerX - updated[i].width / 2, gridSize);
          updated[i] = { ...updated[i], x: snappedX };
        });
      }
      
      return updated;
    });
    toast.success(selectedIndices.size === 1 ? 'Horizontaal gecentreerd op pagina' : 'Horizontaal gecentreerd');
  };

  const alignCenterV = () => {
    if (selectedIndices.size === 0) return;
    
    setPlaceholders(prev => {
      const updated = [...prev];
      
      if (selectedIndices.size === 1) {
        // Single item: center vertically on page
        const idx = Array.from(selectedIndices)[0];
        const snappedY = snapToGridValue((PAGE_HEIGHT - updated[idx].height) / 2, gridSize);
        updated[idx] = { ...updated[idx], y: snappedY };
      } else {
        // Multiple items: center relative to each other
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const minY = Math.min(...selected.map(p => p.y));
        const maxY = Math.max(...selected.map(p => p.y + p.height));
        const centerY = (minY + maxY) / 2;
        selectedIndices.forEach(i => {
          const snappedY = snapToGridValue(centerY - updated[i].height / 2, gridSize);
          updated[i] = { ...updated[i], y: snappedY };
        });
      }
      
      return updated;
    });
    toast.success(selectedIndices.size === 1 ? 'Verticaal gecentreerd op pagina' : 'Verticaal gecentreerd');
  };

  const distributeHorizontally = () => {
    if (selectedIndices.size < 3) {
      toast.error('Selecteer minimaal 3 velden');
      return;
    }
    
    const selected = Array.from(selectedIndices)
      .map(i => ({ index: i, placeholder: placeholders[i] }))
      .sort((a, b) => a.placeholder.x - b.placeholder.x);
    
    const first = selected[0].placeholder;
    const last = selected[selected.length - 1].placeholder;
    const totalSpace = (last.x + last.width) - first.x;
    const totalWidth = selected.reduce((sum, s) => sum + s.placeholder.width, 0);
    const gap = (totalSpace - totalWidth) / (selected.length - 1);
    
    setPlaceholders(prev => {
      const updated = [...prev];
      let currentX = first.x;
      selected.forEach(({ index, placeholder }) => {
        const snappedX = snapToGridValue(currentX, gridSize);
        updated[index] = { ...updated[index], x: snappedX };
        currentX += placeholder.width + gap;
      });
      return updated;
    });
    toast.success('Horizontaal verdeeld');
  };

  const distributeVertically = () => {
    if (selectedIndices.size < 3) {
      toast.error('Selecteer minimaal 3 velden');
      return;
    }
    
    const selected = Array.from(selectedIndices)
      .map(i => ({ index: i, placeholder: placeholders[i] }))
      .sort((a, b) => a.placeholder.y - b.placeholder.y);
    
    const first = selected[0].placeholder;
    const last = selected[selected.length - 1].placeholder;
    const totalSpace = (last.y + last.height) - first.y;
    const totalHeight = selected.reduce((sum, s) => sum + s.placeholder.height, 0);
    const gap = (totalSpace - totalHeight) / (selected.length - 1);
    
    setPlaceholders(prev => {
      const updated = [...prev];
      let currentY = first.y;
      selected.forEach(({ index, placeholder }) => {
        const snappedY = snapToGridValue(currentY, gridSize);
        updated[index] = { ...updated[index], y: snappedY };
        currentY += placeholder.height + gap;
      });
      return updated;
    });
    toast.success('Verticaal verdeeld');
  };

  // Keyboard shortcuts - placed after alignment functions are defined
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Skip if typing in input fields
      const isTyping = document.activeElement?.tagName === 'INPUT' || 
                       document.activeElement?.tagName === 'TEXTAREA';
      
      if (isTyping) return;

      // Undo/Redo - Ctrl+Z / Ctrl+Shift+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          if (canRedo) {
            redoPlaceholders();
            toast.info('Opnieuw');
          }
        } else {
          if (canUndo) {
            undoPlaceholders();
            toast.info('Ongedaan gemaakt');
          }
        }
        return;
      }

      // Alignment shortcuts (Alt + key)
      if (e.altKey && selectedIndices.size > 0) {
        switch (e.key.toLowerCase()) {
          case 'l':
            e.preventDefault();
            alignLeft();
            return;
          case 'r':
            e.preventDefault();
            alignRight();
            return;
          case 't':
            e.preventDefault();
            alignTop();
            return;
          case 'b':
            e.preventDefault();
            alignBottom();
            return;
          case 'c':
            e.preventDefault();
            alignCenterH();
            alignCenterV();
            toast.success('Gecentreerd');
            return;
        }
      }

      // Copy - Ctrl+C
      if (e.key === 'c' && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        copySelected();
        return;
      }

      // Paste - Ctrl+V
      if (e.key === 'v' && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        pasteClipboard();
        return;
      }

      // Duplicate - Ctrl+D (copy + paste in one action)
      if (e.key === 'd' && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        if (selectedIndices.size > 0) {
          // Directly duplicate without going through clipboard
          const duplicates = Array.from(selectedIndices).map(i => ({
            ...placeholders[i],
            x: Math.min(595 - placeholders[i].width, placeholders[i].x + 15),
            y: Math.min(842 - placeholders[i].height, placeholders[i].y + 15),
            isGlobal: false,
          }));
          
          const startIndex = placeholders.length;
          setPlaceholders(prev => [...prev, ...duplicates]);
          setSelectedIndices(new Set(duplicates.map((_, i) => startIndex + i)));
          toast.success(`${duplicates.length} veld(en) gedupliceerd`);
        }
        return;
      }

      // G = toggle grid
      if (e.key === 'g' && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
        setSnapToGrid(prev => {
          const newValue = !prev;
          toast.info(`Grid ${newValue ? 'ingeschakeld' : 'uitgeschakeld'}`);
          return newValue;
        });
      }
      
      // Ctrl+A = select all on current page
      if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const pageIndices = placeholders
          .map((p, i) => (p.page === currentPage || p.isGlobal ? i : -1))
          .filter(i => i !== -1);
        selectMultiple(pageIndices);
        toast.info(`${pageIndices.length} veld(en) geselecteerd`);
      }

      // Delete/Backspace = delete selected
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIndices.size > 0) {
        e.preventDefault();
        deleteSelected();
      }

      // Escape = clear selection
      if (e.key === 'Escape') {
        clearSelection();
      }

      // Arrow keys = nudge selected placeholders
      if (selectedIndices.size > 0 && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const amount = e.shiftKey ? nudgeStepSize * 2 : (snapToGrid ? gridSize : 1);
        
        switch (e.key) {
          case 'ArrowUp':
            handleNudge('up', amount);
            break;
          case 'ArrowDown':
            handleNudge('down', amount);
            break;
          case 'ArrowLeft':
            handleNudge('left', amount);
            break;
          case 'ArrowRight':
            handleNudge('right', amount);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [snapToGrid, selectedIndices, placeholders, currentPage, nudgeStepSize, gridSize, canUndo, canRedo, undoPlaceholders, redoPlaceholders, copySelected, pasteClipboard, setPlaceholders]);

  // Rectangle selection handler
  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (showPreview || !pdfPageRef.current) return;
    
    // Only start selection if clicking on PDF background (not a placeholder)
    if ((e.target as HTMLElement).closest('.placeholder-item')) return;
    
    const pdfRect = pdfPageRef.current.getBoundingClientRect();
    const startX = e.clientX - pdfRect.left;
    const startY = e.clientY - pdfRect.top;
    
    setIsSelecting(true);
    setSelectionRect({ startX, startY, endX: startX, endY: startY });
    
    // Clear selection unless Ctrl is held
    if (!e.ctrlKey && !e.metaKey) {
      clearSelection();
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (!isSelecting || !pdfPageRef.current || !selectionRect) return;
    
    const pdfRect = pdfPageRef.current.getBoundingClientRect();
    const endX = e.clientX - pdfRect.left;
    const endY = e.clientY - pdfRect.top;
    
    setSelectionRect({ ...selectionRect, endX, endY });
  };

  const handleCanvasMouseUp = () => {
    if (!isSelecting || !selectionRect) return;
    
    // Calculate which placeholders are within the selection rectangle
    const minX = Math.min(selectionRect.startX, selectionRect.endX) / scale;
    const maxX = Math.max(selectionRect.startX, selectionRect.endX) / scale;
    const minY = Math.min(selectionRect.startY, selectionRect.endY) / scale;
    const maxY = Math.max(selectionRect.startY, selectionRect.endY) / scale;
    
    const selectedInRect = placeholders
      .map((p, i) => {
        if (p.page !== currentPage) return -1;
        const pCenterX = p.x + p.width / 2;
        const pCenterY = p.y + p.height / 2;
        return (pCenterX >= minX && pCenterX <= maxX && pCenterY >= minY && pCenterY <= maxY) ? i : -1;
      })
      .filter(i => i !== -1);
    
    if (selectedInRect.length > 0) {
      const newSelection = new Set(selectedIndices);
      selectedInRect.forEach(i => newSelection.add(i));
      setSelectedIndices(newSelection);
      toast.info(`${selectedInRect.length} veld(en) geselecteerd`);
    }
    
    setIsSelecting(false);
    setSelectionRect(null);
  };

  // Multi-page management handlers
  const handleAddPage = () => {
    const newPage = localPageCount + 1;
    setLocalPageCount(newPage);
    setLocalPageBackgrounds(prev => [
      ...prev,
      { page: newPage, type: 'blank', color: '#ffffff' }
    ]);
    if (!pdfUrl) setNumPages(newPage);
    toast.success(`Pagina ${newPage} toegevoegd`);
  };

  const handleDeletePage = (pageNumber: number) => {
    if (localPageCount <= 1) {
      toast.error('Minimaal 1 pagina vereist');
      return;
    }
    
    // Update page count
    setLocalPageCount(prev => prev - 1);
    
    // Update page backgrounds (remove page and shift higher pages)
    setLocalPageBackgrounds(prev => 
      prev.filter(bg => bg.page !== pageNumber)
          .map(bg => bg.page > pageNumber ? { ...bg, page: bg.page - 1 } : bg)
    );
    
    // Update placeholders (remove from page and shift higher page numbers)
    setPlaceholders(prev => 
      prev.filter(p => p.page !== pageNumber)
          .map(p => p.page > pageNumber ? { ...p, page: p.page - 1 } : p)
    );
    
    // Adjust current page if needed
    if (currentPage >= pageNumber) {
      setCurrentPage(p => Math.max(1, p - 1));
    }
    
    if (!pdfUrl) setNumPages(prev => prev - 1);
    toast.success(`Pagina ${pageNumber} verwijderd`);
  };

  const handlePageReorder = (oldIndex: number, newIndex: number) => {
    if (oldIndex === newIndex) return;
    
    // Convert from 0-indexed to 1-indexed
    const fromPage = oldIndex + 1;
    const toPage = newIndex + 1;
    
    // Reorder page backgrounds
    setLocalPageBackgrounds(prev => {
      const items = [...prev];
      const [moved] = items.splice(oldIndex, 1);
      items.splice(newIndex, 0, moved);
      // Renumber pages
      return items.map((bg, i) => ({ ...bg, page: i + 1 }));
    });
    
    // Update placeholder page numbers
    setPlaceholders(prev => prev.map(p => {
      if (p.page === fromPage) {
        return { ...p, page: toPage };
      } else if (fromPage < toPage && p.page > fromPage && p.page <= toPage) {
        return { ...p, page: p.page - 1 };
      } else if (fromPage > toPage && p.page >= toPage && p.page < fromPage) {
        return { ...p, page: p.page + 1 };
      }
      return p;
    }));
    
    toast.success('Pagina volgorde bijgewerkt');
  };

  // Effective number of pages - always use the maximum to support extra blank pages
  const effectivePageCount = Math.max(numPages, localPageCount);
  // Filter placeholders: current page + global elements
  const currentPagePlaceholders = placeholders.filter(p => p.page === currentPage || p.isGlobal === true);
  
  // Get current page background config
  const currentPageBackground = localPageBackgrounds.find(bg => bg.page === currentPage) || { 
    page: currentPage, 
    type: 'blank' as const, 
    color: '#ffffff' 
  };

  return (
    <div className="flex h-[80vh] bg-background max-w-full overflow-hidden">
      {/* Left Sidebar: Page Order + Available Placeholders */}
      <div className="flex border-r flex-shrink-0">
        {/* Page Order Panel */}
        <PageOrderPanel
          pageCount={effectivePageCount}
          pageBackgrounds={localPageBackgrounds}
          currentPage={currentPage}
          onPageSelect={setCurrentPage}
          onReorder={handlePageReorder}
          onAddPage={handleAddPage}
          onDeletePage={handleDeletePage}
        />
        
        {/* Placeholders Panel - Collapsible */}
        {leftPanelOpen && (
          <Card className="w-56 min-w-[180px] border-l flex-shrink-0">
            <div className="flex items-center justify-between p-2 border-b">
              <h3 className="font-semibold text-sm">Velden</h3>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setLeftPanelOpen(false)}>
                <PanelLeft className="h-4 w-4" />
              </Button>
            </div>
            <ScrollArea className="h-[calc(100%-44px)]">
              {/* Static Text Section */}
              <div className="p-3 border-b bg-orange-500/5">
                <h4 className="text-xs font-medium text-orange-700 dark:text-orange-400 mb-2 flex items-center gap-1.5">
                  <Type className="h-3.5 w-3.5" />
                  Vaste Tekst
                </h4>
                <div className="flex gap-1.5">
                  <Input
                    value={newStaticText}
                    onChange={(e) => setNewStaticText(e.target.value)}
                    placeholder="Bijv. Project:"
                    className="flex-1 h-7 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newStaticText.trim()) {
                        addStaticText(newStaticText.trim());
                        setNewStaticText('');
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => {
                      if (newStaticText.trim()) {
                        addStaticText(newStaticText.trim());
                        setNewStaticText('');
                      }
                    }}
                    disabled={!newStaticText.trim()}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
                
                {/* Quick examples */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {['Project:', 'Datum:', 'Geldig tot:', 'Offerte:', 'KvK:', 'BTW:'].map(label => (
                    <Button
                      key={label}
                      variant="outline"
                      size="sm"
                      className="h-5 text-[9px] px-1.5 border-orange-300 hover:bg-orange-100 dark:border-orange-700 dark:hover:bg-orange-900/30"
                      onClick={() => addStaticText(label)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Dynamic Placeholders Section */}
              <div className="p-3 space-y-3">
                {availablePlaceholders.reduce((acc, p) => {
                  const categoryIndex = acc.findIndex(c => c.category === p.category);
                  if (categoryIndex === -1) {
                    acc.push({ category: p.category, items: [p] });
                  } else {
                    acc[categoryIndex].items.push(p);
                  }
                  return acc;
                }, [] as Array<{ category: string; items: typeof availablePlaceholders }>).map(group => (
                  <div key={group.category}>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1.5">{group.category}</h4>
                    <div className="space-y-1">
                      {group.items.map((p) => (
                        <PlaceholderHelpPopup key={p.key} placeholderKey={p.key}>
                          <div
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', p.key);
                              handleDragStart(p.key);
                            }}
                            className="p-1.5 bg-muted rounded cursor-move hover:bg-accent text-xs transition-colors group"
                          >
                            <div className="flex items-start justify-between gap-1">
                              <div className="min-w-0 flex-1">
                                <code className="text-[10px] font-mono break-all">{p.key}</code>
                              </div>
                              <HelpCircle className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            </div>
                          </div>
                        </PlaceholderHelpPopup>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </Card>
        )}
        
        {/* Collapsed state toggle */}
        {!leftPanelOpen && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 m-1" 
            onClick={() => setLeftPanelOpen(true)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar - Row 1: Navigation & Actions */}
        <div className="border-b bg-card">
          <div className="p-3 flex items-center justify-between border-b">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm px-1 whitespace-nowrap">
                {currentPage} / {effectivePageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(effectivePageCount, p + 1))}
                disabled={currentPage === effectivePageCount}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              <div className="w-px h-6 bg-border mx-1" />
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                className="h-8 w-8 p-0"
              >
                -
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 px-2 min-w-[60px]">
                    <span className="text-sm">{Math.round(scale * 100)}%</span>
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="min-w-[140px]">
                  <DropdownMenuItem onClick={() => setScale(0.5)}>50%</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setScale(0.75)}>75%</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setScale(1)}>100%</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setScale(1.25)}>125%</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setScale(1.5)}>150%</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => {
                    const containerWidth = scrollContainerRef.current?.clientWidth || 800;
                    const newScale = (containerWidth - 64) / 595;
                    setScale(Math.min(3, Math.max(0.5, newScale)));
                  }}>
                    <ArrowLeftRight className="h-4 w-4 mr-2" />
                    Pas breedte aan
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    const containerHeight = scrollContainerRef.current?.clientHeight || 600;
                    const newScale = (containerHeight - 64) / 842;
                    setScale(Math.min(3, Math.max(0.5, newScale)));
                  }}>
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Pas pagina aan
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale(s => Math.min(2, s + 0.1))}
                className="h-8 w-8 p-0"
              >
                +
              </Button>
              
              <div className="w-px h-6 bg-border mx-1" />
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowPreview(!showPreview);
                  if (!showPreview) {
                    toast.info('Preview Modus: Controls blijven actief');
                  }
                }}
                className="h-8"
              >
                {showPreview ? <EyeOff className="h-4 w-4 mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}
                {showPreview ? 'Designer' : 'Preview'}
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onCancel} className="h-8 text-muted-foreground">
                Annuleren
              </Button>
              <Button size="sm" onClick={() => onSave(placeholders, localPageCount, localPageBackgrounds)} className="h-8">
                Opslaan
              </Button>
            </div>
          </div>

          {/* Toolbar - Row 2: Grid & Alignment Tools */}
          <div className="p-2 flex items-center gap-2 flex-wrap">
            {/* Undo/Redo Controls */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={undoPlaceholders}
                    disabled={!canUndo}
                  >
                    <Undo2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ongedaan maken (Ctrl+Z)</TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={redoPlaceholders}
                    disabled={!canRedo}
                  >
                    <Redo2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Opnieuw (Ctrl+Shift+Z)</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <div className="w-px h-6 bg-border mx-1" />

            {/* Grid Controls */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={snapToGrid ? "default" : "outline"}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setSnapToGrid(!snapToGrid)}
                  >
                    <Grid3x3 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Grid snap aan/uit (G)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {snapToGrid && (
              <Select
                value={gridSize.toString()}
                onValueChange={(v) => setGridSize(Number(v))}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5px</SelectItem>
                  <SelectItem value="10">10px</SelectItem>
                  <SelectItem value="15">15px</SelectItem>
                  <SelectItem value="20">20px</SelectItem>
                  <SelectItem value="25">25px</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Alignment Controls (only when items selected) */}
            {selectedIndices.size > 0 && (
              <>
                <div className="w-px h-6 bg-border mx-1" />
                
                <span className="text-xs text-muted-foreground px-2 whitespace-nowrap">
                  {selectedIndices.size} geselecteerd
                </span>

                {/* Quick Actions: Most used alignment */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-8 w-8" 
                        onClick={alignLeft}
                      >
                        <AlignLeft className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Links uitlijnen</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-8 w-8" 
                        onClick={alignTop}
                      >
                        <AlignStartVertical className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Boven uitlijnen</TooltipContent>
                  </Tooltip>

                  {/* Dropdown for all alignment tools */}
                  <DropdownMenu>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" className="h-8 w-8">
                            <AlignHorizontalJustifyCenter className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent>Meer uitlijnopties</TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Uitlijnen</DropdownMenuLabel>
                      <DropdownMenuItem onClick={alignLeft}>
                        <AlignLeft className="mr-2 h-4 w-4" />
                        Links uitlijnen
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={alignCenterH}>
                        <AlignHorizontalJustifyCenter className="mr-2 h-4 w-4" />
                        Horizontaal centreren
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={alignRight}>
                        <AlignRight className="mr-2 h-4 w-4" />
                        Rechts uitlijnen
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={alignTop}>
                        <AlignStartVertical className="mr-2 h-4 w-4" />
                        Boven uitlijnen
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={alignCenterV}>
                        <AlignVerticalJustifyCenter className="mr-2 h-4 w-4" />
                        Verticaal centreren
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={alignBottom}>
                        <AlignEndVertical className="mr-2 h-4 w-4" />
                        Onder uitlijnen
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Verdelen</DropdownMenuLabel>
                      <DropdownMenuItem 
                        onClick={distributeHorizontally}
                        disabled={selectedIndices.size < 3}
                      >
                        <ArrowLeftRight className="mr-2 h-4 w-4" />
                        Horizontaal verdelen {selectedIndices.size < 3 && '(min. 3)'}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={distributeVertically}
                        disabled={selectedIndices.size < 3}
                      >
                        <ArrowUpDown className="mr-2 h-4 w-4" />
                        Verticaal verdelen {selectedIndices.size < 3 && '(min. 3)'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Copy button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={copySelected}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Kopiëren (Ctrl+C)</TooltipContent>
                  </Tooltip>

                  {/* Paste button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={pasteClipboard}
                        disabled={clipboard.length === 0}
                      >
                        <ClipboardPaste className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Plakken (Ctrl+V)</TooltipContent>
                  </Tooltip>

                  {/* Delete button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={deleteSelected}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Verwijderen (Delete)</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
         </div>

        {/* PDF Container + Right Sidebar */}
        <div className="flex flex-1 min-h-0">
          {/* PDF Viewer */}
          <div ref={scrollContainerRef} className="flex-1 p-8 bg-muted/50 overflow-auto" style={{ maxWidth: '100%' }}>
            <div className="flex justify-center min-h-full">
              <div
              ref={pdfPageRef}
              className="relative inline-block bg-white shadow-2xl select-none touch-action-none"
              style={{ maxWidth: '100%', margin: 'auto', position: 'relative' }}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
            >
              {/* Render PDF page or blank A4 page */}
              {pdfUrl ? (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages: pdfPages }) => {
                    // KRITIEK: Respecteer initialPageCount als deze groter is dan PDF pages
                    // Dit zorgt dat extra blanco pagina's behouden blijven bij heropenen
                    const desiredPageCount = Math.max(pdfPages, initialPageCount || 1);
                    console.log('[PDF Load Success]', { 
                      pdfPages, 
                      initialPageCount,
                      desiredPageCount 
                    });
                    // Sync both numPages AND localPageCount to the desired value
                    setNumPages(desiredPageCount);
                    setLocalPageCount(desiredPageCount);
                    
                    // Informatieve melding
                    if (desiredPageCount > pdfPages) {
                      toast.success(`PDF geladen (${pdfPages} PDF-pagina${pdfPages !== 1 ? "'s" : ''}, ${desiredPageCount - pdfPages} extra blanco)`);
                    } else {
                      toast.success(`PDF geladen (${pdfPages} pagina${pdfPages !== 1 ? "'s" : ''})`);
                    }
                  }}
                  onLoadError={(error) => {
                    console.error('[PDF Load Error]', error);
                    toast.error(`PDF laden mislukt`);
                  }}
                >
                  <Page
                    pageNumber={currentPage}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={595 * scale}
                  />
                </Document>
              ) : (
                /* Blank A4 page for templates without PDF */
                <div 
                  style={{ 
                    width: 595 * scale, 
                    height: 842 * scale, 
                    backgroundColor: currentPageBackground.color || '#ffffff',
                    border: '1px solid hsl(var(--border))'
                  }}
                  className="flex items-center justify-center"
                >
                  <div className="text-center text-muted-foreground">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs opacity-50">Blanco pagina {currentPage}</p>
                  </div>
                </div>
              )}

              {/* Grid Overlay */}
              {snapToGrid && !showPreview && (
                <svg
                  className="absolute inset-0 pointer-events-none opacity-20"
                  style={{ width: 595 * scale, height: 842 * scale }}
                >
                  <defs>
                    <pattern
                      id="grid-pattern"
                      x="0"
                      y="0"
                      width={gridSize * scale}
                      height={gridSize * scale}
                      patternUnits="userSpaceOnUse"
                    >
                      <circle
                        cx={gridSize * scale / 2}
                        cy={gridSize * scale / 2}
                        r="1"
                        fill="currentColor"
                        className="text-primary"
                      />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid-pattern)" />
                </svg>
              )}

              {/* Overlay: Draggable Placeholders */}
              {!showPreview && currentPagePlaceholders.map((placeholder, idx) => {
                const globalIndex = placeholders.indexOf(placeholder);
                const selected = isSelected(globalIndex);
                return (
                  <Draggable
                    key={`${placeholder.key}-${globalIndex}`}
                    position={{ x: placeholder.x * scale, y: placeholder.y * scale }}
                onStart={(e, data) => {
                  // CRITICAL: Commit current state to history BEFORE drag starts
                  // This ensures undo restores to the pre-drag position
                  commitPlaceholderHistory();
                  
                  // Initialize drag session
                  const draggingIndices = selected && selectedIndices.size > 1 
                    ? selectedIndices 
                    : new Set([globalIndex]);
                  
                  const startPositions = new Map<number, { x: number; y: number }>();
                  draggingIndices.forEach(i => {
                    startPositions.set(i, { x: placeholders[i].x, y: placeholders[i].y });
                  });
                  
                  dragSessionRef.current = {
                    isDragging: true,
                    selectedIndices: draggingIndices,
                    startPositions
                  };
                  isDraggingPlaceholderRef.current = true;
                }}
                    onDrag={(e, data) => {
                      if (!dragSessionRef.current) return;

                      // Throttle updates with requestAnimationFrame
                      if (rafPendingRef.current) return;
                      rafPendingRef.current = true;

                      requestAnimationFrame(() => {
                        rafPendingRef.current = false;
                        
                        if (!dragSessionRef.current) return;
                        
                        const { selectedIndices: draggingIndices, startPositions } = dragSessionRef.current;
                        const anchorStart = startPositions.get(globalIndex);
                        
                        if (!anchorStart) {
                          console.error('[Drag] Missing anchor start position', {
                            globalIndex,
                            startPositions: Array.from(startPositions.keys())
                          });
                          return;
                        }

                        // Calculate delta in unscaled units
                        const deltaX = (data.x / scale) - anchorStart.x;
                        const deltaY = (data.y / scale) - anchorStart.y;

                        // Log first drag event for debugging
                        if (Math.abs(deltaX) < 2 && Math.abs(deltaY) < 2) {
                          console.log('[Drag] Initial drag', {
                            globalIndex,
                            anchorStart,
                            dataPosition: { x: data.x, y: data.y },
                            scale,
                            delta: { deltaX, deltaY },
                            draggingCount: draggingIndices.size
                          });
                        }

                        // Update all dragging placeholders with magnetic snap guides
                        setPlaceholdersLive(prev => {
                          const updated = [...prev];
                          let activeSnapGuides = { vertical: [] as number[], horizontal: [] as number[] };
                          
                          draggingIndices.forEach(i => {
                            const start = startPositions.get(i);
                            if (!start) return;
                            
                            // Calculate new position
                            let newX = start.x + deltaX;
                            let newY = start.y + deltaY;
                            
                            // Apply magnetic snapping
                            const snapResult = calculateSnapGuides(
                              i, 
                              newX, 
                              newY, 
                              updated[i].width, 
                              updated[i].height
                            );
                            
                            newX = snapResult.x;
                            newY = snapResult.y;
                            
                            // Merge snap guides
                            activeSnapGuides.vertical.push(...snapResult.guides.vertical);
                            activeSnapGuides.horizontal.push(...snapResult.guides.horizontal);
                            
                            // Clamp to page bounds
                            const clamped = clampToPage(newX, newY, updated[i].width, updated[i].height);
                            
                            updated[i] = { ...updated[i], x: clamped.x, y: clamped.y };
                          });
                          
                          // Update visible snap guides (deduplicated)
                          setSnapGuides({
                            vertical: [...new Set(activeSnapGuides.vertical)],
                            horizontal: [...new Set(activeSnapGuides.horizontal)]
                          });
                          
                          return updated;
                        });
                      });
                    }}
                    onStop={(e, data) => {
                      try {
                        if (!dragSessionRef.current) {
                          return;
                        }
                        
                        const { selectedIndices: draggingIndices } = dragSessionRef.current;
                        
                        // Snap and clamp all dragged placeholders
                        commitSnap(draggingIndices);
                        
                        // Clear snap guides
                        setSnapGuides({ vertical: [], horizontal: [] });
                        
                        // Clear drag session
                        dragSessionRef.current = null;
                        
                        // Reset click suppression after a short delay
                        setTimeout(() => {
                          isDraggingPlaceholderRef.current = false;
                        }, 50);
                      } catch (error) {
                        console.error('[DragStop] Error:', error);
                        toast.error('Fout bij verplaatsen veld');
                      }
                    }}
                    bounds="parent"
                  >
                    <div
                      className={cn(
                        "border-2 cursor-move transition-all placeholder-item select-none touch-action-none relative",
                        placeholder.key.startsWith('{{static_text_')
                          ? "border-orange-500 bg-orange-500/10"
                          : placeholder.isGlobal 
                            ? "border-amber-500 bg-amber-500/10" 
                            : "border-primary bg-primary/10",
                        selected && placeholder.key.startsWith('{{static_text_') && "ring-2 ring-orange-500 shadow-lg bg-orange-500/20",
                        selected && !placeholder.key.startsWith('{{static_text_') && placeholder.isGlobal && "ring-2 ring-amber-500 shadow-lg bg-amber-500/20",
                        selected && !placeholder.key.startsWith('{{static_text_') && !placeholder.isGlobal && "ring-2 ring-primary shadow-lg bg-primary/20"
                      )}
                      style={{
                        width: placeholder.width * scale,
                        height: placeholder.height * scale,
                        position: 'absolute',
                        left: 0,
                        top: 0,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelection(globalIndex, e.ctrlKey || e.metaKey, e.shiftKey);
                      }}
                    >
                      {/* Static text indicator badge */}
                      {placeholder.key.startsWith('{{static_text_') && (
                        <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-[8px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 z-10">
                          <Type className="h-2.5 w-2.5" />
                          Tekst
                        </div>
                      )}
                      
                      {/* Global indicator badge */}
                      {placeholder.isGlobal && !placeholder.key.startsWith('{{static_text_') && (
                        <div className="absolute -top-2 -left-2 bg-amber-500 text-white text-[8px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 z-10">
                          <Globe className="h-2.5 w-2.5" />
                          Globaal
                        </div>
                      )}
                      
                      {/* Special rendering for different placeholder types */}
                      {placeholder.key.startsWith('{{static_text_') ? (
                        <div className="flex items-center h-full px-1 overflow-hidden">
                          <span className="text-[10px] font-medium truncate" style={{ color: placeholder.color }}>
                            {placeholder.staticValue || 'Tekst...'}
                          </span>
                        </div>
                      ) : placeholder.key === '{{company_logo}}' ? (
                        <div className="flex items-center justify-center h-full bg-muted/50 gap-1">
                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-[10px] font-medium text-muted-foreground">Logo</span>
                        </div>
                      ) : placeholder.key === '{{line_items_table}}' ? (
                        <div className="flex flex-col items-center justify-center h-full bg-muted/50 text-center p-1">
                          <Table className="h-5 w-5 text-muted-foreground mb-1" />
                          <span className="text-[10px] font-medium text-muted-foreground">Regelitems Tabel</span>
                        </div>
                      ) : (
                        <span className="text-[10px] font-mono px-1 truncate block">
                          {placeholder.key}
                        </span>
                      )}

                      {/* Resize Handle */}
                      <div
                        className="absolute bottom-0 right-0 w-3 h-3 bg-primary cursor-se-resize"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          const startX = e.clientX;
                          const startY = e.clientY;
                          const startWidth = placeholder.width;
                          const startHeight = placeholder.height;

                          const handleMouseMove = (moveEvent: MouseEvent) => {
                            const deltaX = (moveEvent.clientX - startX) / scale;
                            const deltaY = (moveEvent.clientY - startY) / scale;
                            handleResize(
                              globalIndex,
                              Math.max(50, startWidth + deltaX),
                              Math.max(20, startHeight + deltaY)
                            );
                          };

                          const handleMouseUp = () => {
                            // Final snap when user releases mouse
                            const snapped = snapDimensions(placeholder.width, placeholder.height);
                            handleResize(globalIndex, snapped.width, snapped.height);
                            
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };

                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                        }}
                      />
                    </div>
                  </Draggable>
                );
              })}

              {/* Preview Mode: Render with Sample Data */}
              {showPreview && currentPagePlaceholders.map((placeholder, idx) => {
                try {
                  const globalIndex = placeholders.indexOf(placeholder);
                  
                  // Debug log ALL placeholders
                  console.log('[Preview] Rendering placeholder', {
                    idx,
                    globalIndex,
                    key: placeholder.key,
                    rawPosition: { x: placeholder.x, y: placeholder.y },
                    scaledPosition: { 
                      x: placeholder.x * scale, 
                      y: placeholder.y * scale 
                    },
                    transform: `translate(${placeholder.x * scale}px, ${placeholder.y * scale}px)`,
                    scale,
                    containerInfo: 'Direct child of PDF container'
                  });
                  
                  return (
                    <div
                      key={idx}
                      className="overflow-hidden cursor-not-allowed"
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        transform: `translate(${placeholder.x * scale}px, ${placeholder.y * scale}px)`,
                        width: placeholder.width * scale,
                        height: placeholder.height * scale,
                        fontSize: placeholder.fontSize * scale,
                        fontWeight: placeholder.fontWeight,
                        color: placeholder.color,
                        textAlign: placeholder.align,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '2px 4px',
                        pointerEvents: 'auto',
                      }}
                      onClick={() => {
                        console.warn('[Preview] Placeholder clicked in preview mode (non-interactive)', {
                          key: placeholder.key,
                          globalIndex
                        });
                        toast.info('Preview modus: velden zijn niet interactief. Schakel naar Designer modus om te bewerken.');
                      }}
                    >
                      {/* Render static text with actual value in preview */}
                      {placeholder.key.startsWith('{{static_text_') ? (
                        placeholder.staticValue || 'Tekst...'
                      ) : placeholder.key === '{{company_logo}}' ? (
                        <div className="flex items-center justify-center h-full bg-muted/30 gap-1 border border-dashed border-muted-foreground rounded">
                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Logo</span>
                        </div>
                      ) : placeholder.key === '{{line_items_table}}' ? (
                        <div className="flex flex-col h-full bg-muted/30 border border-dashed border-muted-foreground rounded p-2 text-xs">
                          <div className="flex gap-2 text-muted-foreground font-medium border-b pb-1 mb-1">
                            <span className="flex-1">Omschrijving</span>
                            <span className="w-12 text-right">Aantal</span>
                            <span className="w-16 text-right">Prijs</span>
                            <span className="w-16 text-right">Totaal</span>
                          </div>
                          <div className="flex gap-2 text-muted-foreground/60">
                            <span className="flex-1">Voorbeeld regelitem...</span>
                            <span className="w-12 text-right">1</span>
                            <span className="w-16 text-right">€100</span>
                            <span className="w-16 text-right">€100</span>
                          </div>
                        </div>
                      ) : (
                        getSampleData(placeholder.key)
                      )}
                    </div>
                  );
                } catch (error) {
                  console.error('[Preview] Fout bij renderen placeholder:', {
                    error,
                    message: error instanceof Error ? error.message : 'Onbekende fout',
                    idx,
                    placeholder
                  });
                  return null;
                }
              })}

              {/* Snap Guide Lines */}
              {snapGuides.vertical.map((x, i) => (
                <div
                  key={`vguide-${i}`}
                  className="absolute bg-blue-500 pointer-events-none z-50"
                  style={{
                    left: x * scale,
                    top: 0,
                    width: 1,
                    height: PAGE_HEIGHT * scale,
                    opacity: 0.7
                  }}
                />
              ))}
              {snapGuides.horizontal.map((y, i) => (
                <div
                  key={`hguide-${i}`}
                  className="absolute bg-blue-500 pointer-events-none z-50"
                  style={{
                    left: 0,
                    top: y * scale,
                    width: PAGE_WIDTH * scale,
                    height: 1,
                    opacity: 0.7
                  }}
                />
              ))}

              {/* Rectangle Selection Overlay */}
              {isSelecting && selectionRect && (
                <div
                  className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
                  style={{
                    left: Math.min(selectionRect.startX, selectionRect.endX),
                    top: Math.min(selectionRect.startY, selectionRect.endY),
                    width: Math.abs(selectionRect.endX - selectionRect.startX),
                    height: Math.abs(selectionRect.endY - selectionRect.startY),
                  }}
                />
              )}

              {/* Drop Zone Hint */}
              {isDragging && (
                <div className="absolute inset-0 bg-primary/5 border-2 border-dashed border-primary flex items-center justify-center pointer-events-none">
                  <p className="text-primary font-medium">Sleep hier om toe te voegen</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar: Properties + Quick Add - Horizontally Collapsible */}
        {rightSidebarOpen ? (
          <Card className="w-56 min-w-[200px] border-l flex-shrink-0 flex flex-col h-full overflow-hidden">
            {/* Collapse button */}
            <div className="p-1.5 border-b flex justify-end">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6"
                onClick={() => setRightSidebarOpen(false)}
                title="Sidebar inklappen"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-3">
                {/* Selection Controls - Show first when something is selected */}
                {selectedIndices.size === 1 ? (
                  <div className="space-y-3">
                    <NudgeControls
                      onNudge={handleNudge}
                      onCenter={handleCenter}
                      stepSize={nudgeStepSize}
                      setStepSize={setNudgeStepSize}
                    />
                    <PlaceholderPropertyEditor
                      placeholder={placeholders[Array.from(selectedIndices)[0]]}
                      onChange={(updates) => handlePlaceholderUpdate(Array.from(selectedIndices)[0], updates)}
                      onDelete={() => handlePlaceholderDelete(Array.from(selectedIndices)[0])}
                    />
                  </div>
                ) : selectedIndices.size > 1 ? (
                  <div className="space-y-3">
                    <NudgeControls
                      onNudge={handleNudge}
                      onCenter={handleCenter}
                      stepSize={nudgeStepSize}
                      setStepSize={setNudgeStepSize}
                    />
                    <div className="space-y-2">
                      <div className="text-center">
                        <p className="text-sm font-semibold">{selectedIndices.size} velden geselecteerd</p>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="w-full"
                        onClick={deleteSelected}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Verwijderen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-center py-2 px-3 bg-muted/50 rounded-md">
                      <p className="text-xs text-muted-foreground">
                        Selecteer een veld om te bewerken
                      </p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1">
                        Gebruik ←↑→↓ om te verplaatsen
                      </p>
                    </div>
                    
                    {/* Collapsible Keyboard Shortcuts */}
                    <KeyboardShortcutsPanel />
                  </div>
                )}

                {/* Quick Add Section - Always available at bottom */}
                <div className="pt-2 border-t">
                  <QuickAddSection 
                    availablePlaceholders={availablePlaceholders}
                    currentPage={currentPage}
                    snapPosition={snapPosition}
                    setPlaceholders={setPlaceholders}
                    placeholders={placeholders}
                    setSelectedIndices={setSelectedIndices}
                    setLastSelectedIndex={setLastSelectedIndex}
                  />
                </div>
              </div>
            </ScrollArea>
          </Card>
        ) : (
          <div className="w-8 border-l bg-card flex flex-col items-center pt-2 flex-shrink-0">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6"
              onClick={() => setRightSidebarOpen(true)}
              title="Sidebar uitklappen"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

// Quick Add Section Component
const QuickAddSection = ({
  availablePlaceholders,
  currentPage,
  snapPosition,
  setPlaceholders,
  placeholders,
  setSelectedIndices,
  setLastSelectedIndex,
}: {
  availablePlaceholders: ReturnType<typeof getAllPlaceholderKeys>;
  currentPage: number;
  snapPosition: (x: number, y: number) => { x: number; y: number };
  setPlaceholders: React.Dispatch<React.SetStateAction<PlaceholderPosition[]>>;
  placeholders: PlaceholderPosition[];
  setSelectedIndices: React.Dispatch<React.SetStateAction<Set<number>>>;
  setLastSelectedIndex: React.Dispatch<React.SetStateAction<number | null>>;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  
  const filteredPlaceholders = availablePlaceholders.filter(p => 
    p.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleQuickAdd = (key: string) => {
    const tempPlaceholder = createDefaultPlaceholder(key, 0, 0);
    const centerX = (595 - tempPlaceholder.width) / 2;
    const centerY = (842 - tempPlaceholder.height) / 2;
    const snapped = snapPosition(centerX, centerY);
    
    const newPlaceholder = createDefaultPlaceholder(key, snapped.x, snapped.y);
    newPlaceholder.page = currentPage;

    setPlaceholders(prev => {
      const newArray = [...prev, newPlaceholder];
      return newArray;
    });
    
    const newIndex = placeholders.length;
    setSelectedIndices(new Set([newIndex]));
    setLastSelectedIndex(newIndex);
    
    toast.success(`Veld "${key}" toegevoegd`);
    setSearchQuery('');
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between h-8 px-2">
          <span className="flex items-center gap-1.5 text-xs font-medium">
            <Plus className="h-3.5 w-3.5" />
            Snel toevoegen
          </span>
          {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 space-y-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Zoek veld..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-7 h-8 text-xs"
          />
        </div>
        
        <div className="max-h-40 overflow-y-auto space-y-0.5">
          {filteredPlaceholders.slice(0, 10).map((p) => (
            <PlaceholderHelpPopup key={p.key} placeholderKey={p.key}>
              <button
                type="button"
                onClick={() => handleQuickAdd(p.key)}
                className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors flex items-center gap-2 group"
              >
                <Plus className="h-3 w-3 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] truncate">{p.key}</div>
                  <div className="text-[9px] text-muted-foreground truncate">{p.description}</div>
                </div>
                <HelpCircle className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            </PlaceholderHelpPopup>
          ))}
          {filteredPlaceholders.length === 0 && searchQuery && (
            <p className="text-xs text-muted-foreground text-center py-2">Geen velden gevonden</p>
          )}
          {filteredPlaceholders.length > 10 && (
            <p className="text-[10px] text-muted-foreground text-center py-1">
              +{filteredPlaceholders.length - 10} meer...
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Keyboard Shortcuts Panel Component
const KeyboardShortcutsPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between h-7 px-2">
          <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <Info className="h-3 w-3" />
            Sneltoetsen
          </span>
          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1.5 space-y-0.5">
        <KeyboardShortcut keys={['Ctrl', 'Z']} description="Ongedaan maken" />
        <KeyboardShortcut keys={['Ctrl', 'Shift', 'Z']} description="Opnieuw" />
        <KeyboardShortcut keys={['Alt', 'L']} description="Links uitlijnen" />
        <KeyboardShortcut keys={['Alt', 'R']} description="Rechts uitlijnen" />
        <KeyboardShortcut keys={['Alt', 'T']} description="Boven uitlijnen" />
        <KeyboardShortcut keys={['Alt', 'B']} description="Onder uitlijnen" />
        <KeyboardShortcut keys={['Alt', 'C']} description="Centreren" />
        <KeyboardShortcut keys={['G']} description="Grid aan/uit" />
        <KeyboardShortcut keys={['←', '↑', '→', '↓']} description="Verplaats" />
        <KeyboardShortcut keys={['Del']} description="Verwijderen" />
        <KeyboardShortcut keys={['Ctrl', 'A']} description="Alles selecteren" />
        <KeyboardShortcut keys={['Esc']} description="Deselecteren" />
      </CollapsibleContent>
    </Collapsible>
  );
};
